import { isAdmin } from '@/constants/roles';
import { MessageContext } from '@/contexts/MessageContext';
import { isEqual } from 'lodash';
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
    ChainlitContext,
    IAction,
    IFeedback,
    IMessageElement,
    IStep,
    IThread,
    nestMessages,
    sessionIdState,
    sideViewState,
    useApi,
    useConfig
} from '@chainlit/react-client';

import { useAuth } from '@/hooks/useAuth';
import { useLayoutMaxWidth } from 'hooks/useLayoutMaxWidth';

import { PodcastStatus, podcastState } from '@/state/podcast';

import { ErrorBoundary } from './ErrorBoundary';
import ThreadSkeleton from './chat/ThreadSkeleton';
import { Messages } from './chat/Messages';
import PodcastPlayer from './chat/PodcastPlayer';

type Props = {
    id: string;
};

const ReadOnlyThread = ({ id }: Props) => {
    const { config } = useConfig();
    const {
        data: thread,
        error: threadError,
        isLoading
    } = useApi<IThread>(id ? `/project/thread/${id}` : null, {
        revalidateOnFocus: false
    });
    const navigate = useNavigate();
    const setSideView = useSetRecoilState(sideViewState);
    const setPodcast = useSetRecoilState(podcastState);
    const [steps, setSteps] = useState<IStep[]>([]);
    const apiClient = useContext(ChainlitContext);
    const { t } = useTranslation();
    const layoutMaxWidth = useLayoutMaxWidth();
    const sessionId = useRecoilValue(sessionIdState);
    const previousElementsRef = useRef<IMessageElement[]>();
    const { user } = useAuth();

    // Check if user can view podcasts (only requires organization membership)
    const canViewPodcast = useMemo(() => {
        if (!user) return false;
        const isEnabledForOrg = config?.features?.podcast_enabled !== false;
        return isEnabledForOrg;
    }, [user, config]);

    // Clear sideView when component mounts or id changes
    useEffect(() => {
        previousElementsRef.current = undefined;
        setSideView(undefined);
    }, [id, setSideView]);

    // Fetch podcast status for pinned thread
    useEffect(() => {
        if (!id || !canViewPodcast) {
            setPodcast((prev) => ({ ...prev, status: null, isLoading: false }));
            return;
        }

        const fetchPodcastStatus = async () => {
            setPodcast((prev) => ({ ...prev, isLoading: true, error: null }));

            try {
                const response = await fetch(`/podcast/status/${id}`, {
                    method: 'GET',
                    credentials: 'include'
                });

                if (response.status === 403) {
                    // User not authorized
                    setPodcast((prev) => ({
                        ...prev,
                        isLoading: false,
                        status: null
                    }));
                    return;
                }

                if (!response.ok) {
                    throw new Error('Failed to fetch podcast status');
                }

                const status: PodcastStatus = await response.json();
                setPodcast((prev) => ({
                    ...prev,
                    isLoading: false,
                    status,
                    error: null
                }));
            } catch (error) {
                console.error('Error fetching podcast status:', error);
                setPodcast((prev) => ({
                    ...prev,
                    isLoading: false,
                    error: 'Failed to load podcast status'
                }));
            }
        };

        fetchPodcastStatus();
    }, [id, canViewPodcast, setPodcast]);

    useEffect(() => {
        if (!thread) {
            setSteps([]);
            return;
        }
        setSteps(thread.steps);

        // Set elements to sideView if they exist and have changed
        const currentElements = thread.elements as IMessageElement[];
        if (thread.elements && thread.elements.length > 0) {
            // Only update if elements actually changed (deep equality check)
            if (!isEqual(previousElementsRef.current, currentElements)) {
                previousElementsRef.current = currentElements;
                setSideView({
                    title: 'Charts',
                    elements: currentElements
                });
            }
        } else if (previousElementsRef.current) {
            // Clear if we had elements before but don't now
            previousElementsRef.current = undefined;
            setSideView(undefined);
        }
    }, [thread, setSideView]);

    useEffect(() => {
        if (threadError) {
            navigate('/');
            toast.error('Failed to load thread: ' + threadError.message);
        }
    }, [threadError]);

    const onFeedbackUpdated = useCallback(
        async (message: IStep, onSuccess: () => void, feedback: IFeedback) => {
            toast.promise(apiClient.setFeedback(feedback, sessionId), {
                loading: 'Updating',
                success: (res) => {
                    setSteps((prev) =>
                        prev.map((step) => {
                            if (step.id === message.id) {
                                return {
                                    ...step,
                                    feedback: {
                                        ...feedback,
                                        id: res.feedbackId
                                    }
                                };
                            }
                            return step;
                        })
                    );

                    onSuccess();
                    return 'Feedback updated!';
                },
                error: (err) => {
                    return <span>{err.message}</span>;
                }
            });
        },
        [setSteps]
    );

    const onFeedbackDeleted = useCallback(
        async (message: IStep, onSuccess: () => void, feedbackId: string) => {
            toast.promise(apiClient.deleteFeedback(feedbackId), {
                loading: t('chat.messages.feedback.status.updating'),
                success: () => {
                    setSteps((prev) =>
                        prev.map((step) => {
                            if (step.id === message.id) {
                                return {
                                    ...step,
                                    feedback: undefined
                                };
                            }
                            return step;
                        })
                    );

                    onSuccess();
                    return t('chat.messages.feedback.status.updated');
                },
                error: (err) => {
                    return <span>{err.message}</span>;
                }
            });
        },
        [setSteps]
    );

    const onElementRefClick = useCallback(
        (element: IMessageElement) => {
            if (element.display === 'side') {
                setSideView({ title: element.name, elements: [element] });
                return;
            }

            let path = `/element/${element.id}`;

            if (element.threadId) {
                path += `?thread=${element.threadId}`;
            }

            return navigate(element.display === 'page' ? path : '#');
        },
        [setSideView, navigate]
    );

    const onError = useCallback((error: string) => toast.error(error), [toast]);

    const elements = thread?.elements || [];
    const actions: IAction[] = [];
    const messages = nestMessages(steps);

    const memoizedContext = useMemo(() => {
        return {
            allowHtml: config?.features?.unsafe_allow_html,
            latex: config?.features?.latex,
            editable: false,
            loading: false,
            showFeedbackButtons: false,
            uiName: config?.ui?.name || '',
            cot: config?.ui?.cot || 'hidden',
            onElementRefClick,
            onError,
            onFeedbackUpdated,
            onFeedbackDeleted
        };
    }, [
        config?.ui?.name,
        config?.ui?.cot,
        config?.features?.unsafe_allow_html,
        onElementRefClick,
        onError,
        onFeedbackUpdated,
        onFeedbackDeleted
    ]);

    if (isLoading) {
        return (
            <ThreadSkeleton showComposer={false} />
        );
    }

    if (!thread) {
        return null;
    }

    const formattedDate = thread?.createdAt
        ? new Date(thread.createdAt.replace('Z', '')).toLocaleString(
              undefined,
              {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
              }
          )
        : '';

    return (
        <div className="relative flex flex-col grow w-full overflow-y-auto">
            <ErrorBoundary>
                <MessageContext.Provider value={memoizedContext}>
                    {/* Header for pinned thread */}
                    <div className="flex justify-center items-center px-3 w-full">
                        <p className="py-2 text-muted-foreground text-sm text-center">
                            This thread was pinned by {thread?.userIdentifier}
                            {formattedDate && (
                                <>
                                    <br />
                                    at: {formattedDate}
                                </>
                            )}
                        </p>
                    </div>

                    <div
                        className="flex flex-col grow mx-auto p-4 w-full"
                        style={{
                            maxWidth: layoutMaxWidth
                        }}
                    >
                        <Messages
                            indent={0}
                            messages={messages}
                            elements={elements as any}
                            actions={actions}
                        />
                    </div>

                    {/* Show podcast player at bottom if user can view podcasts */}
                    {canViewPodcast && (
                        <div className="sticky bottom-0 left-0 right-0 bg-background border-t">
                            <div
                                className="mx-auto p-4"
                                style={{
                                    maxWidth: layoutMaxWidth
                                }}
                            >
                                <PodcastPlayer />
                            </div>
                        </div>
                    )}
                </MessageContext.Provider>
            </ErrorBoundary>
        </div>
    );
};

export { ReadOnlyThread };
